import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../api/api";
import "./Login.css";

function Signup() {
  const navigate = useNavigate();

  // Steps: "details" (Name, Email, Password) -> "otp" (Email Verification Code)
  const [step, setStep] = useState("details");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    otp: "",
  });

  const isValidEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email),
    [form.email]
  );

  const isValidOtp = useMemo(
    () => /^\d{4,6}$/.test(form.otp),
    [form.otp]
  );

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const resetMessages = () => {
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    resetMessages();

    if (name === "otp") {
      setForm((prev) => ({ ...prev, otp: value.replace(/\D/g, "").slice(0, 6) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async () => {
    resetMessages();

    if (!form.name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (!isValidEmail) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!form.password || form.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    try {
      setLoading(true);

      const res = await api.post("/api/auth/signup/send-otp", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });

      setMessage(res.data?.message || "OTP sent to your email.");
      setStep("otp");
      setResendTimer(30);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    resetMessages();

    if (!isValidOtp) {
      setError("Please enter a valid OTP code.");
      return;
    }

    try {
      setLoading(true);

      const res = await api.post("/api/auth/signup/verify", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        otp: form.otp,
      });

      if (res.data?.token) {
        localStorage.setItem("token", res.data.token);
      }
      
      if (res.data?.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      setMessage(res.data?.message || "Account created successfully.");
      
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (err) {
      setError(err.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Create Account</h2>
        <p className="auth-subtext">
          {step === "otp" ? "Enter the verification code sent to your email." : "Sign up with email verification."}
        </p>

        {step === "details" && (
          <>
            <div className="auth-field">
              <label htmlFor="signup-name">Full Name</label>
              <input
                id="signup-name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={form.name}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="auth-field">
              <label htmlFor="signup-email">Email Address</label>
              <input
                id="signup-email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="auth-field">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                name="password"
                type="password"
                placeholder="Create a password (min 6 chars)"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </>
        )}

        {step === "otp" && (
          <>
            <div className="auth-field">
              <label htmlFor="signup-email-readonly">Email Address</label>
              <input
                id="signup-email-readonly"
                value={form.email}
                disabled
                readOnly
              />
            </div>

            <div className="auth-field">
              <label htmlFor="signup-otp">Email Verification Code</label>
              <input
                id="signup-otp"
                name="otp"
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit OTP"
                value={form.otp}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </>
        )}

        {message && <p className="auth-message success">{message}</p>}
        {error && <p className="auth-message error">{error}</p>}

        {step === "details" ? (
          <button className="auth-btn" onClick={handleSendOtp} disabled={loading}>
            {loading ? "Sending Code..." : "Continue & Send OTP"}
          </button>
        ) : (
          <>
            <button className="auth-btn" onClick={handleVerifyOtp} disabled={loading}>
              {loading ? "Verifying..." : "Verify & Create Account"}
            </button>

            <button
              className="auth-btn secondary"
              onClick={handleSendOtp}
              disabled={loading || resendTimer > 0}
              type="button"
            >
              {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : "Resend Code"}
            </button>

            <button
              className="auth-link-btn"
              type="button"
              onClick={() => {
                setStep("details");
                setForm((prev) => ({ ...prev, otp: "" }));
                resetMessages();
              }}
              disabled={loading}
            >
              Edit details
            </button>
          </>
        )}

        {step === "details" && (
          <div className="auth-footer" style={{ marginTop: '20px', textAlign: 'center', fontSize: '0.9rem' }}>
            <p>
              Already have an account?{" "}
              <Link to="/login" style={{ color: '#007bff', fontWeight: 'bold', textDecoration: 'none' }}>
                Log in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Signup;